import { useSyncExternalStore } from 'react';
import dayjs from 'dayjs';
import { getBranches } from 'api/Branch';
import { getPosOrder, getPosOrders } from 'api/PosV2';
import { useExecLog } from '../AppTuVan/execLog';
import type { ServerVisit } from '../AppTuVan/receptionServer';
import { orderFromApi } from '../POS/remote';
import { apiRequest } from '../Telesales/lead/api';
import usePosStore from '../POS/store';
import type { Order, Transaction } from '../POS/data';
import useTreatmentStore from '../Treatment/store';
import { usageItems } from '../Treatment/logic';
import { DEFAULT_RULES, scopeError, validateVoucher } from './ktBooks';
import { billOfCard, caseBlock, depositInfo, hasTeam, journal, refundQuoteOf, sessionErrors, syncCards, usageBlock, type CaseInput } from './ktCards';
import { draftLines, draftPayErrors, draftSaveErrors, paymentErrors, SURCHARGE, type SurchargeType } from './ktCounter';
import { demoState, TODAY_SAMPLE_BILLS } from './ktDemo';
import { applyExecLog, applyExecVisits } from './ktExec';
import { syncOrders } from './ktPos';
import {
  applyCancel, applyEdit, billNo, COUNTER_ROLE, COUNTER_USERS, dayCloseErrors, flagInvoice, invoiceCheck, invoiceNo, modifyBlock, needsRef, pendingTxns, shiftCloseErrors, validatePatch, ymd, TIER,
  type BankRow, type Bill, type BillDraft, type BillPatch, type BookScope, type Buyer, type CardGate, type CommissionRate, type DraftLine, type KtState, type PayMethod, type Payment, type PayooRow, type Shift, type Team, type Tier, type Voucher,
} from './ktLogic';

/** Dữ liệu demo kế toán lưu ở localStorage — sau này thay từng hàm ktApi bằng API */

const KEY = 'khothi.ketoan.v1';

function load(): KtState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      // Bảng Mapping lưu trên máy từ bản trước: bổ sung dòng mặc định mới (vd hoàn tiền qua ngân hàng)
      if (s?.v === 5 && Array.isArray(s.bills)) return { ...s, bills: s.bills.filter((b: Bill) => !(b.source === 'demo' && TODAY_SAMPLE_BILLS.includes(b.id))), rules: [...s.rules, ...DEFAULT_RULES.filter(d => !s.rules.some((r: { id: string }) => r.id === d.id))] };
    }
  } catch { /* dữ liệu hỏng → nạp lại demo */ }
  const s = freshDemo();
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* bỏ qua */ }
  return s;
}

/** Dữ liệu đầu: đơn POS đang có trên máy + các tình huống mẫu */
const posData = () => { const p = usePosStore.getState(); return { orders: p.orders, transactions: p.transactions }; };
const cardSource = () => {
  const p = usePosStore.getState();
  const t = useTreatmentStore.getState();
  return { treatment: t.cards, packages: t.packages, cfg: t.config, pos: { orders: p.orders, transactions: p.transactions, refunds: p.refunds, customers: p.customers } };
};
function freshDemo() {
  const src = cardSource();
  return demoState(Date.now(), posData(), { treatment: src.treatment, packages: src.packages, refunds: src.pos.refunds, customers: src.pos.customers }, { todaySamples: false });
}

/**
 * Nạp dữ liệu và bắt đầu nhận đơn POS / thẻ Liệu trình ở lần đầu có màn Kế toán (hoặc ktApi) dùng tới.
 * Các trang Kế toán import tĩnh trong App: chạy ngay lúc import thì mọi người dùng, ở mọi màn (kể cả POS),
 * đều nạp dữ liệu mẫu và tính lại sổ sau mỗi thay đổi của POS.
 */
let state: KtState | undefined;
const cur = (): KtState => {
  if (!state) {
    state = load();
    startSync();
  }
  return state;
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function set(fn: (s: KtState) => KtState) {
  state = fn(cur());
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* bỏ qua */ }
  emit();
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useKt = () => useSyncExternalStore(subscribe, cur);

const SERVER_PAGE = 50;
const VISIT_PAGE = 200;
const VISIT_MAX_PAGES = 5;
const execRecords = () => Object.values(useExecLog.getState().records);
const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
export const today = () => ymd(Date.now());
const userOf = (s: KtState) => COUNTER_USERS.find(u => u.id === s.counterUser) || COUNTER_USERS[0];
/** Nhật ký ghi đúng người ở quầy, các tầng khác ghi theo vai */
const who = (s: KtState) => (s.tier === 'counter' ? `${userOf(s).name} (${COUNTER_ROLE[userOf(s).role]})` : TIER[s.tier].short);
/** Vai và giờ hiện tại để xét khung giờ hỗ trợ của Sale Offline */
export const ktActor = (s: KtState) => (s.tier === 'counter' ? { role: userOf(s).role, hm: dayjs().format('HH:mm') } : undefined);
const nextSeq = (s: KtState) => Math.max(0, ...s.bills.map(b => b.seq)) + 1;
const normalize = (payments: Payment[]) => payments.map(p => ({ ...p, ref: p.ref.trim().toUpperCase() }));
const patchBill = (s: KtState, id: string, fn: (b: Bill) => Bill): KtState => ({ ...s, bills: s.bills.map(b => (b.id === id ? fn(b) : b)) });

export interface Result { ok: boolean; errors?: string[]; id?: string }
const fail = (...errors: string[]): Result => ({ ok: false, errors });

export interface DraftInput { id?: string; customer: string; phone: string; kind: BillDraft['kind']; sale: string; lines: DraftLine[]; orderDiscount: number; payments: Payment[]; note: string }

export const ktApi = {
  snapshot: () => cur(),
  setTier(tier: Tier) { set(s => ({ ...s, tier })); },
  resetDemo() { set(() => freshDemo()); },

  /** Nhận đơn POS trên máy này: đơn mới, thu nợ, hoàn tiền, hủy đơn. Chạy tự động mỗi khi POS đổi */
  syncPos() {
    const r = syncOrders(cur(), posData(), 'pos', Date.now());
    const orders = usePosStore.getState().orders.length;
    if (!r.added && !r.changed && r.state === cur()) {
      if (cur().posSync.orders !== orders) set(x => ({ ...x, posSync: { ...x.posSync, at: Date.now(), orders } }));
      return { added: 0, changed: 0 };
    }
    set(() => ({ ...r.state, posSync: { ...r.state.posSync, at: Date.now(), orders } }));
    return { added: r.added, changed: r.changed };
  },

  /** Đọc đơn POS hôm nay trên server (máy thu ngân là máy khác): chỉ đọc, đơn đã có thì chỉ nhận thay đổi */
  async pullServerOrders() {
    const date = today();
    const list = await getPosOrders({ from: date, to: date, per_page: SERVER_PAGE });
    if (!list.ok) {
      set(x => ({ ...x, posSync: { ...x.posSync, serverError: list.message || 'Không đọc được đơn POS trên server' } }));
      return { added: 0, changed: 0, error: true };
    }
    const orders: Order[] = [];
    const transactions: Transaction[] = [];
    const appointments = new Map<string, number>();
    await Promise.all((list.data?.items || []).map(async it => {
      const d = await getPosOrder(it.order_id);
      if (!d.ok || !d.data) return;
      const m = orderFromApi(d.data);
      orders.push({ ...m.order, status: it.status === 'cancelled' || it.pos_status === 'CANCELLED' ? 'cancelled' : m.order.status });
      transactions.push(...m.transactions);
      if (d.data.appointment_id) appointments.set(m.order.id, d.data.appointment_id);
    }));
    const r = syncOrders(cur(), { orders, transactions }, 'server', Date.now());
    const bills = r.state.bills.map(b => (b.posOrderId && appointments.has(b.posOrderId) ? { ...b, appointmentId: appointments.get(b.posOrderId) } : b));
    set(() => ({ ...r.state, bills, posSync: { ...r.state.posSync, serverAt: Date.now(), serverOrders: orders.length, serverError: undefined } }));
    return { added: r.added, changed: r.changed, error: false };
  },

  /** Đọc lượt hôm nay của Lễ tân rồi ghi người thực hiện vào bill POS: chỉ đọc, không ghi ngược */
  async pullExecVisits() {
    const fail = (execError: string) => { set(x => ({ ...x, posSync: { ...x.posSync, execError } })); return { error: true }; };
    const branches: any = await getBranches().catch(() => null); // eslint-disable-line @typescript-eslint/no-explicit-any
    const raw: { id: number }[] = Array.isArray(branches?.data?.items) ? branches.data.items : Array.isArray(branches?.data) ? branches.data : [];
    if (!raw.length) return fail('Không đọc được danh sách cơ sở');
    const visits: ServerVisit[] = [];
    for (const branch of raw) {
      for (let page = 1; page <= VISIT_MAX_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await apiRequest<{ items: ServerVisit[]; pagination: { total: number } }>(`/api/v2/reception/visits?branch_id=${branch.id}&per_page=${VISIT_PAGE}&page=${page}`);
        if (!res.ok || !res.data) return fail(res.message || 'Không đọc được lượt của Lễ tân');
        visits.push(...res.data.items);
        if (res.data.items.length < VISIT_PAGE) break;
      }
    }
    set(x => {
      const next = applyExecLog(applyExecVisits(x, visits, Date.now()), execRecords(), Date.now());
      return { ...next, posSync: { ...next.posSync, execAt: Date.now(), execVisits: visits.filter(v => v.tech || v.doctor).length, execError: undefined } };
    });
    return { error: false };
  },

  /** Nhận bản ghi ekip, mã máy theo dịch vụ Lễ tân vừa lưu */
  syncExecLog() {
    const next = applyExecLog(cur(), execRecords(), Date.now());
    if (next !== cur()) set(() => next);
  },

  /** Đọc thẻ từ Liệu trình và đơn POS, lượt trừ mới vào nhật ký riêng */
  syncCards() {
    const next = syncCards(cur(), cardSource(), Date.now());
    if (next !== cur()) set(() => next);
  },

  dismissPosAlert(id: string) { set(s => ({ ...s, posAlerts: s.posAlerts.filter(a => a.id !== id) })); },

  // ---------- Quầy: người thao tác, tạm tính ----------

  setCounterUser(counterUser: string) { set(s => ({ ...s, counterUser })); },

  /** Kế toán quầy bật khi đông khách để Sale Offline hỗ trợ sửa, hủy bill trước 17h30 */
  setRush(on: boolean): Result {
    const s = cur();
    if (s.tier !== 'counter' || userOf(s).role !== 'cashier') return fail('Chỉ Kế toán quầy bật được chế độ đông khách');
    const date = today();
    set(x => {
      const rushDays = { ...x.rushDays };
      if (on) rushDays[date] = { by: who(x), at: Date.now() }; else delete rushDays[date];
      return { ...x, rushDays };
    });
    return { ok: true };
  },

  /** Tạm tính chưa cấp số bill: khách đổi ý thì bỏ, chuỗi số chứng từ không bị ảnh hưởng */
  saveDraft(input: DraftInput): Result {
    if (cur().tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline lập tạm tính');
    const errors = draftSaveErrors(input);
    if (errors.length) return fail(...errors);
    const at = Date.now();
    const id = input.id || uid();
    set(x => {
      const old = x.drafts.find(d => d.id === input.id);
      const body = { customer: input.customer.trim(), phone: input.phone.trim(), kind: input.kind, sale: input.sale, lines: input.lines, orderDiscount: input.orderDiscount, payments: input.payments, note: input.note, updatedAt: at };
      if (old) return { ...x, drafts: x.drafts.map(d => (d.id === id ? { ...d, ...body } : d)) };
      return { ...x, drafts: [{ ...body, id, no: `TT-${String(x.drafts.length + 1).padStart(4, '0')}`, at, by: who(x), status: 'draft' as const }, ...x.drafts] };
    });
    return { ok: true, id };
  },

  dropDraft(id: string, reason: string): Result {
    if (cur().tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline bỏ tạm tính');
    if (reason.trim().length < 5) return fail('Nhập lý do (tối thiểu 5 ký tự)');
    set(x => ({ ...x, drafts: x.drafts.map(d => (d.id === id && d.status === 'draft' ? { ...d, status: 'dropped', reason: reason.trim(), updatedAt: Date.now() } : d)) }));
    return { ok: true };
  },

  /** Thu chính thức: lúc này mới cấp số bill, luôn mang ngày hôm nay nên không chèn lùi được */
  payDraft(id: string): Result {
    const s = cur();
    if (s.tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline thu tiền');
    const d = s.drafts.find(x => x.id === id && x.status === 'draft');
    if (!d) return fail('Không tìm thấy tạm tính');
    const date = today();
    if (s.locks[date]) return fail('Hôm nay đã khóa sổ, không tạo thêm bill được');
    const errors = draftPayErrors(s, d);
    if (errors.length) return fail(...errors);
    const at = Date.now();
    const seq = nextSeq(s);
    const bill: Bill = {
      id: uid(), seq, no: billNo(seq), date, at, customer: d.customer, phone: d.phone, branchId: 'q1', source: 'manual', debt: 0, refunds: [], kind: d.kind, sale: d.sale || undefined,
      lines: draftLines(d), payments: normalize(d.payments), status: 'valid', createdBy: who(s), log: [{ at, by: who(s), action: `Thu tiền từ tạm tính ${d.no}` }], invoice: { status: 'none' },
    };
    set(x => ({ ...x, bills: [...x.bills, bill], drafts: x.drafts.map(y => (y.id === id ? { ...y, status: 'paid', billId: bill.id, updatedAt: at } : y)) }));
    return { ok: true, id: bill.id };
  },

  /** Phiếu phụ thu là chứng từ riêng: sửa, hủy không đụng tới bill chính */
  createSurcharge(input: { type: SurchargeType; customer: string; phone: string; parentBillId?: string; amount: number; payments: Payment[]; note: string }): Result {
    const s = cur();
    if (s.tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline lập phiếu phụ thu');
    const date = today();
    if (s.locks[date]) return fail('Hôm nay đã khóa sổ, không tạo thêm phiếu được');
    if (!input.customer.trim()) return fail('Nhập tên khách');
    if (!(input.amount > 0)) return fail('Số tiền phải lớn hơn 0');
    const errors = paymentErrors(s, input.payments, input.amount);
    if (errors.length) return fail(...errors);
    const t = SURCHARGE[input.type];
    const at = Date.now();
    const seq = nextSeq(s);
    const parent = s.bills.find(b => b.id === input.parentBillId);
    const bill: Bill = {
      id: uid(), seq, no: billNo(seq), date, at, customer: input.customer.trim(), phone: input.phone.trim(), branchId: 'q1', source: 'manual', debt: 0, refunds: [], kind: 'surcharge', parentBillId: parent?.id,
      lines: [{ id: uid(), serviceId: t.serviceId, name: t.label, gross: input.amount }], payments: normalize(input.payments), status: 'valid', createdBy: who(s),
      log: [{ at, by: who(s), action: `Lập phiếu ${t.label}`, reason: [parent ? `Theo bill ${parent.no}` : '', input.note.trim()].filter(Boolean).join('. ') || undefined }], invoice: { status: 'none' },
    };
    set(x => ({ ...x, bills: [...x.bills, bill] }));
    return { ok: true, id: bill.id };
  },

  editBill(id: string, patch: BillPatch, reason: string): Result {
    const s = cur();
    const b = s.bills.find(x => x.id === id);
    if (!b) return fail('Không tìm thấy bill');
    const block = modifyBlock(s, b, s.tier, today(), ktActor(s));
    if (block) return fail(block);
    const errors = validatePatch(patch, reason);
    if (errors.length) return fail(...errors);
    set(x => patchBill(x, id, bb => applyEdit(bb, patch, who(x), reason, Date.now())));
    return { ok: true };
  },

  cancelBill(id: string, reason: string): Result {
    const s = cur();
    const b = s.bills.find(x => x.id === id);
    if (!b) return fail('Không tìm thấy bill');
    const block = modifyBlock(s, b, s.tier, today(), ktActor(s));
    if (block) return fail(block);
    if (reason.trim().length < 5) return fail('Nhập lý do (tối thiểu 5 ký tự)');
    set(x => patchBill(x, id, bb => applyCancel(bb, who(x), reason, Date.now())));
    return { ok: true };
  },

  lockDay(date: string): Result {
    const s = cur();
    if (s.tier !== 'revenue') return fail('Chỉ Kế toán nội bộ bán hàng được duyệt khóa sổ');
    const errors = [...dayCloseErrors(s, date), ...shiftCloseErrors(s, date)];
    if (errors.length) return fail(...errors);
    const at = Date.now();
    set(x => ({ ...x, locks: { ...x.locks, [date]: { by: who(x), at } }, lockLog: [...x.lockLog, { date, action: 'lock', by: who(x), at }] }));
    return { ok: true };
  },

  unlockDay(date: string, reason: string): Result {
    const s = cur();
    if (s.tier !== 'chief') return fail('Chỉ Kế toán trưởng được mở khóa');
    if (reason.trim().length < 5) return fail('Nhập lý do mở khóa (tối thiểu 5 ký tự)');
    set(x => {
      const locks = { ...x.locks };
      delete locks[date];
      return { ...x, locks, lockLog: [...x.lockLog, { date, action: 'unlock', by: who(x), at: Date.now(), reason: reason.trim() }] };
    });
    return { ok: true };
  },

  setPayoo(payoo: PayooRow[]) { set(s => ({ ...s, payoo })); },
  setBank(bank: BankRow[]) { set(s => ({ ...s, bank })); },

  /** Giả lập phát hành: chưa nối EasyInvoice, chỉ cấp số hóa đơn và ghi ngược vào bill */
  issueInvoices(ids: string[]): Result {
    const s = cur();
    const ok = ids.filter(id => {
      const b = s.bills.find(x => x.id === id);
      return b && b.invoice.status === 'none' && !invoiceCheck(s, b).errors.length;
    });
    if (!ok.length) return fail('Không có bill nào đủ điều kiện phát hành');
    const at = Date.now();
    set(x => {
      let seq = x.invoiceSeq;
      const bills = x.bills.map(b => {
        if (!ok.includes(b.id)) return b;
        seq += 1;
        const no = invoiceNo(seq);
        return { ...b, invoice: { status: 'issued' as const, no, at }, log: [...b.log, { at, by: who(x), action: `Phát hành hóa đơn ${no}` }] };
      });
      return { ...x, bills, invoiceSeq: seq };
    });
    return { ok: true };
  },

  /** Hóa đơn đã xuất có sai: lập hóa đơn điều chỉnh hoặc thay thế, không có thao tác hủy */
  fixInvoice(id: string, kind: 'adjust' | 'replace'): Result {
    const at = Date.now();
    const b = cur().bills.find(x => x.id === id);
    if (!b || b.invoice.status !== 'needs_adjust') return fail('Hóa đơn này không cần xử lý');
    set(x => {
      const seq = x.invoiceSeq + 1;
      const no = invoiceNo(seq);
      const label = kind === 'adjust' ? 'điều chỉnh' : 'thay thế';
      return {
        ...patchBill(x, id, bb => ({
          ...bb,
          invoice: kind === 'adjust' ? { ...bb.invoice, status: 'adjusted', adjustNo: no } : { ...bb.invoice, status: 'replaced', replaceNo: no },
          log: [...bb.log, { at, by: who(x), action: `Lập hóa đơn ${label} ${no}`, reason: bb.invoice.flagReason }],
        })),
        invoiceSeq: seq,
      };
    });
    return { ok: true };
  },

  /** Khách cần hóa đơn đích danh; không nhập là khách lẻ */
  setBuyer(id: string, buyer: Buyer | undefined): Result {
    const b = cur().bills.find(x => x.id === id);
    if (!b) return fail('Không tìm thấy bill');
    if (b.invoice.status !== 'none') return fail('Hóa đơn đã phát hành: đổi người mua bằng hóa đơn thay thế');
    set(x => patchBill(x, id, bb => ({ ...bb, buyer, log: [...bb.log, { at: Date.now(), by: who(x), action: buyer ? `Ghi người mua: ${buyer.name}` : 'Bỏ thông tin người mua' }] })));
    return { ok: true };
  },

  /** Gộp đơn cọc gốc vào bill dùng dịch vụ để xuất một hóa đơn */
  mergeDeposit(id: string, depositId: string): Result {
    const b = cur().bills.find(x => x.id === id);
    const d = cur().bills.find(x => x.id === depositId);
    if (!b || !d || d.kind !== 'deposit') return fail('Không tìm thấy đơn cọc');
    if (b.invoice.status !== 'none') return fail('Hóa đơn đã phát hành, không gộp thêm được');
    if (depositInfo(cur(), d, today()).state === 'overdue') return fail(`Cọc ${d.no} đã quá hạn: Kế toán trưởng gia hạn trước khi dùng`);
    set(x => patchBill(x, id, bb => ({ ...bb, depositOf: depositId, log: [...bb.log, { at: Date.now(), by: who(x), action: `Gộp cọc ${d.no}` }] })));
    return { ok: true };
  },

  /** Thêm thuế suất có ngày hiệu lực cho một hoặc nhiều mã */
  addRate(serviceIds: string[], rate: number | null, from: string, note: string): Result {
    const s = cur();
    if (s.tier !== 'chief') return fail('Chỉ Kế toán trưởng được đổi thuế suất');
    if (!serviceIds.length) return fail('Chọn ít nhất một mã');
    if (!from) return fail('Chọn ngày hiệu lực');
    const dup = serviceIds.find(id => s.rates.some(r => r.serviceId === id && r.from === from));
    if (dup) return fail(`Mã ${s.services.find(x => x.id === dup)?.code} đã có thuế suất cho ngày hiệu lực đó`);
    set(x => ({ ...x, rates: [...x.rates, ...serviceIds.map(serviceId => ({ id: uid(), serviceId, rate, from, note }))] }));
    return { ok: true };
  },

  /** Kế toán trưởng sửa bảng Mapping; bút toán sinh lại theo bảng mới */
  updateRule(id: string, debit: string, credit: string, scope: BookScope): Result {
    if (cur().tier !== 'chief') return fail('Chỉ Kế toán trưởng được sửa bảng Mapping');
    if (!/^\d{3,5}$/.test(debit) || !/^\d{3,5}$/.test(credit)) return fail('Số tài khoản gồm 3 đến 5 chữ số');
    const guard = scopeError(debit, credit, scope);
    if (guard) return fail(guard);
    set(x => ({ ...x, rules: x.rules.map(r => (r.id === id ? { ...r, debit, credit, scope } : r)) }));
    return { ok: true };
  },

  addVoucher(input: Pick<Voucher, 'date' | 'ruleId' | 'desc' | 'amount' | 'scope' | 'reason' | 'costCenter' | 'nonDeductible'>): Result {
    if (cur().tier === 'counter') return fail('Kế toán quầy không lập chứng từ tổng hợp');
    if (cur().locks[input.date]) return fail('Ngày này đã khóa sổ');
    const errors = validateVoucher(cur(), input);
    if (errors.length) return fail(...errors);
    set(x => {
      const no = `CT-${String(x.vouchers.length + 100).padStart(4, '0')}`;
      return { ...x, vouchers: [...x.vouchers, { ...input, id: uid(), no, branchId: 'q1', by: who(x), at: Date.now() }] };
    });
    return { ok: true };
  },

  // ---------- Thẻ liệu trình, nhật ký trừ thẻ ----------

  /** Ghi ca: qua chốt chặn cứng, không có lối duyệt vượt. Thẻ bên Liệu trình thì trừ qua đúng hàm của Liệu trình */
  recordSession(input: { cardId: string; serviceId: string; qty: number; team: Team; machine: string; note: string; shots?: number }): Result {
    const s = cur();
    if (s.tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline ghi ca');
    const date = today();
    const errors = usageBlock(s, input.cardId, input.serviceId, input.qty, date);
    const card = s.cards.find(c => c.id === input.cardId);
    if (!card) return fail('Không tìm thấy thẻ');
    errors.push(...sessionErrors(input.team, [card.lines.find(l => l.serviceId === input.serviceId)?.group || ''], input.machine));
    if (errors.length) return fail(...errors);
    const req = [{ serviceId: input.serviceId, qty: input.qty }];
    const inTreatment = useTreatmentStore.getState().cards.some(c => c.id === input.cardId);
    let usageId = '';
    if (inTreatment) {
      const u = useTreatmentStore.getState().deduct(input.cardId, { items: req, branchId: 'q1', staffId: '', note: input.note, signed: true, by: who(s) });
      if (!u) return fail('Không trừ được buổi');
      usageId = u.id;
      ktApi.syncCards();
    } else {
      usageId = `kt-use-${uid()}`;
      const usage = { id: usageId, at: new Date().toISOString(), branchId: 'q1', staffId: '', items: usageItems(card, req), by: who(s), note: input.note, signed: true };
      set(x => journal({ ...x, cards: x.cards.map(c => (c.id === input.cardId ? { ...c, usages: [...c.usages, usage] } : c)) }, Date.now()));
    }
    set(x => ({ ...x, usageLog: x.usageLog.map(e => (e.id === usageId ? { ...e, team: input.team, machine: input.machine.trim(), shots: input.shots } : e)) }));
    return { ok: true };
  },

  /** Ghi ca của một dòng dịch vụ lẻ: ghi đè có nhật ký cũ, mới. Sổ đã khóa thì chỉ Kế toán nội bộ sửa được */
  recordLineSession(billId: string, lineId: string, input: { team: Team; machine: string; shots?: number }): Result {
    const s = cur();
    const b = s.bills.find(x => x.id === billId);
    const l = b?.lines.find(x => x.id === lineId);
    if (!b || !l) return fail('Không tìm thấy dòng dịch vụ');
    if (s.locks[b.date] ? s.tier !== 'revenue' : s.tier !== 'counter') return fail(s.locks[b.date] ? 'Sổ đã khóa, chỉ Kế toán nội bộ sửa ca được' : 'Chỉ Kế toán quầy, Sale Offline ghi ca');
    const errors = sessionErrors(input.team, [s.services.find(x => x.id === l.serviceId)?.group || ''], input.machine);
    if (errors.length) return fail(...errors);
    const machine = input.machine.trim();
    const text = (t?: Team) => Object.values(t || {}).filter(Boolean).join(', ') || '(trống)';
    const changes = [
      { field: 'Ekip', from: text(l.team || b.team), to: text(input.team) },
      { field: 'Mã máy', from: l.machine || '(trống)', to: machine || '(trống)' },
    ].filter(c => c.from !== c.to);
    set(x => patchBill(x, billId, bb => ({
      ...bb,
      lines: bb.lines.map(y => (y.id === lineId ? { ...y, team: input.team, machine, shots: input.shots } : y)),
      log: [...bb.log, { at: Date.now(), by: who(x), action: `Ghi ca: ${l.name}`, changes }],
    })));
    return { ok: true };
  },

  /** Bổ sung ekip, máy cho lượt trừ thẻ */
  recordUsageSession(id: string, input: { team: Team; machine: string; shots?: number }): Result {
    const s = cur();
    const e = s.usageLog.find(x => x.id === id);
    if (!e) return fail('Không tìm thấy lượt');
    if (s.locks[e.date] ? s.tier !== 'revenue' : s.tier !== 'counter') return fail(s.locks[e.date] ? 'Sổ đã khóa, chỉ Kế toán nội bộ sửa ca được' : 'Chỉ Kế toán quầy, Sale Offline ghi ca');
    const card = s.cards.find(c => c.id === e.cardId);
    const groups = e.items.map(i => card?.lines.find(l => l.serviceId === i.serviceId)?.group || '');
    const errors = sessionErrors(input.team, groups, input.machine);
    if (errors.length) return fail(...errors);
    set(x => ({ ...x, usageLog: x.usageLog.map(u => (u.id === id ? { ...u, team: input.team, machine: input.machine.trim(), shots: input.shots } : u)) }));
    return { ok: true };
  },

  // ---------- Chốt ca ----------

  /** Bắt buộc nhập tiền mặt kiểm kê; lệch thì phải có lý do. Mã giao dịch thiếu vẫn chốt được nhưng sổ ngày không khóa được */
  closeShift(input: { name: string; openingCash: number; cashCounted: number | null; diffReason: string }): Result {
    const s = cur();
    if (s.tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline chốt ca');
    if (input.cashCounted === null || !(input.cashCounted >= 0)) return fail('Bắt buộc nhập số tiền mặt kiểm kê thực tế');
    const txns = pendingTxns(s);
    if (!txns.length) return fail('Không có giao dịch nào chưa chốt');
    const cash = txns.filter(t => t.method === 'cash').reduce((t, x) => t + x.amount, 0);
    const cashExpected = input.openingCash + cash;
    const diff = input.cashCounted - cashExpected;
    if (diff && input.diffReason.trim().length < 5) return fail('Tiền mặt bị lệch: nhập lý do (tối thiểu 5 ký tự)');
    const at = Date.now();
    const shift = (n: number): Shift => ({
      id: uid(), no: `CA-${String(n).padStart(5, '0')}`, date: today(), name: input.name, by: who(s), at, openingCash: input.openingCash, txns,
      cashExpected, cashCounted: input.cashCounted!, diff, diffReason: diff ? input.diffReason.trim() : '', status: 'closed',
    });
    set(x => ({ ...x, shifts: [shift(x.shifts.length + 1), ...x.shifts] }));
    return { ok: true };
  },

  /** Kế toán nội bộ nhận biên bản, bảng kê: đủ điều kiện để khóa sổ */
  receiveShift(id: string): Result {
    const s = cur();
    if (s.tier !== 'revenue') return fail('Kế toán nội bộ nhận biên bản');
    if (s.shifts.find(x => x.id === id)?.status !== 'closed') return fail('Ca này không ở trạng thái chờ nhận');
    set(x => ({ ...x, shifts: x.shifts.map(y => (y.id === id ? { ...y, status: 'received', receivedBy: who(x), receivedAt: Date.now() } : y)) }));
    return { ok: true };
  },

  /** Mở lại ca để quầy sửa bill: biên bản cũ giữ nguyên để truy vết, giao dịch về ca chốt lại */
  reopenShift(id: string, reason: string): Result {
    const s = cur();
    const x0 = s.shifts.find(x => x.id === id);
    if (!x0 || x0.status === 'reopened') return fail('Ca này không mở lại được');
    if (s.tier !== 'revenue') return fail('Kế toán nội bộ mở lại ca');
    if (x0.txns.some(t => s.locks[t.date])) return fail('Sổ đã khóa: Kế toán trưởng mở khóa sổ trước');
    if (reason.trim().length < 5) return fail('Nhập lý do (tối thiểu 5 ký tự)');
    set(x => ({ ...x, shifts: x.shifts.map(y => (y.id === id ? { ...y, status: 'reopened', reopenedBy: who(x), reopenedAt: Date.now(), reopenReason: reason.trim() } : y)) }));
    return { ok: true };
  },

  /** Hủy lượt trong ngày: giữ nguyên số nhật ký, sổ đã khóa thì không hủy được */
  cancelSession(id: string, reason: string): Result {
    const s = cur();
    const e = s.usageLog.find(x => x.id === id);
    if (!e) return fail('Không tìm thấy lượt');
    if (s.tier !== 'counter') return fail('Chỉ Kế toán quầy, Sale Offline hủy lượt');
    if (s.locks[e.date]) return fail('Sổ đã khóa, chỉ Kế toán trưởng mở được');
    if (e.date !== today()) return fail('Chỉ được hủy trong ngày làm việc');
    if (reason.trim().length < 5) return fail('Nhập lý do (tối thiểu 5 ký tự)');
    const cancelled = { at: new Date().toISOString(), by: who(s), reason: reason.trim() };
    if (useTreatmentStore.getState().cards.some(c => c.id === e.cardId)) useTreatmentStore.getState().cancelUsage(e.cardId, id, reason.trim(), who(s));
    set(x => ({
      ...x,
      usageLog: x.usageLog.map(u => (u.id === id ? { ...u, cancelled: reason.trim() } : u)),
      cards: x.cards.map(c => (c.id === e.cardId ? { ...c, usages: c.usages.map(u => (u.id === id ? { ...u, cancelled } : u)) } : c)),
    }));
    return { ok: true };
  },

  setCardGate(cardGate: CardGate): Result {
    if (cur().tier !== 'chief') return fail('Chỉ Kế toán trưởng đổi chốt chặn');
    set(x => ({ ...x, cardGate }));
    return { ok: true };
  },

  // ---------- Cọc ----------

  extendDeposit(id: string, until: string, reason: string): Result {
    if (cur().tier !== 'chief') return fail('Chỉ Kế toán trưởng gia hạn cọc');
    if (!until) return fail('Chọn hạn mới');
    if (reason.trim().length < 5) return fail('Nhập lý do (tối thiểu 5 ký tự)');
    set(x => patchBill(x, id, b => ({ ...b, depositUntil: until, log: [...b.log, { at: Date.now(), by: who(x), action: `Gia hạn cọc đến ${until}`, reason: reason.trim() }] })));
    return { ok: true };
  },

  // ---------- Hoàn tiền, hủy thẻ ----------

  createRefundCase(cardId: string, input: CaseInput & { reason: string }): Result {
    const s = cur();
    if (s.tier === 'chief') return fail('Quầy hoặc Kế toán nội bộ lập hồ sơ, Kế toán trưởng chỉ duyệt');
    const card = s.cards.find(c => c.id === cardId);
    if (!card) return fail('Chọn thẻ');
    const block = caseBlock(s, card);
    if (block) return fail(block);
    if (input.reason.trim().length < 5) return fail('Nhập lý do (tối thiểu 5 ký tự)');
    const q = refundQuoteOf(s, card, input);
    const n = s.refundCases.length + 1;
    const at = Date.now();
    set(x => ({
      ...x,
      refundCases: [...x.refundCases, {
        id: uid(), no: `HT-${String(n).padStart(4, '0')}`, cardId, billId: billOfCard(x, card)?.id, at, reason: input.reason.trim(), status: 'pending_l1', penaltyPct: input.penaltyPct, ...q,
        steps: [{ tier: x.tier, by: who(x), at, action: 'Tạo hồ sơ' }],
      }],
    }));
    return { ok: true };
  },

  /** Cấp 1: Kế toán nội bộ. Cấp 2: Kế toán trưởng. Không cấp nào duyệt thay cấp nào */
  approveCase(id: string): Result {
    const s = cur();
    const c = s.refundCases.find(x => x.id === id);
    if (!c) return fail('Không tìm thấy hồ sơ');
    const next = c.status === 'pending_l1' && s.tier === 'revenue' ? 'pending_l2' as const : c.status === 'pending_l2' && s.tier === 'chief' ? 'approved' as const : null;
    if (!next) return fail(c.status === 'pending_l1' ? 'Hồ sơ đang chờ Kế toán nội bộ duyệt' : c.status === 'pending_l2' ? 'Hồ sơ đang chờ Kế toán trưởng duyệt' : 'Hồ sơ không ở bước duyệt');
    set(x => ({ ...x, refundCases: x.refundCases.map(r => (r.id === id ? { ...r, status: next, steps: [...r.steps, { tier: x.tier, by: who(x), at: Date.now(), action: next === 'pending_l2' ? 'Duyệt cấp 1' : 'Duyệt cấp 2' }] } : r)) }));
    return { ok: true };
  },

  rejectCase(id: string, note: string): Result {
    const s = cur();
    const c = s.refundCases.find(x => x.id === id);
    if (!c) return fail('Không tìm thấy hồ sơ');
    if (!((c.status === 'pending_l1' && s.tier === 'revenue') || (c.status === 'pending_l2' && s.tier === 'chief'))) return fail('Hồ sơ không ở bước duyệt của vai này');
    if (note.trim().length < 5) return fail('Nhập lý do từ chối (tối thiểu 5 ký tự)');
    set(x => ({ ...x, refundCases: x.refundCases.map(r => (r.id === id ? { ...r, status: 'rejected', steps: [...r.steps, { tier: x.tier, by: who(x), at: Date.now(), action: 'Từ chối', note: note.trim() }] } : r)) }));
    return { ok: true };
  },

  /** Ghi nhận đã chi: bút toán, truy thu hoa hồng và báo điều chỉnh hóa đơn tự sinh theo hồ sơ */
  payCase(id: string, method: PayMethod, ref: string): Result {
    const s = cur();
    const c = s.refundCases.find(x => x.id === id);
    if (!c || c.status !== 'approved') return fail('Hồ sơ chưa được duyệt đủ 2 cấp');
    if (s.tier !== 'revenue') return fail('Kế toán nội bộ xác nhận đã chi');
    if (needsRef(method) && !ref.trim()) return fail('Bắt buộc nhập mã giao dịch');
    const date = today();
    if (s.locks[date]) return fail('Sổ hôm nay đã khóa');
    const at = Date.now();
    set(x => ({
      ...x,
      refundCases: x.refundCases.map(r => (r.id === id ? { ...r, status: 'paid', method, ref: ref.trim().toUpperCase(), paidDate: date, steps: [...r.steps, { tier: x.tier, by: who(x), at, action: 'Xác nhận đã chi' }] } : r)),
      cards: x.cards.map(card => (card.id === c.cardId ? { ...card, status: 'refunded' } : card)),
      bills: x.bills.map(b => (b.id === c.billId ? { ...b, invoice: flagInvoice(b.invoice, `Hủy thẻ, hoàn tiền theo hồ sơ ${c.no}`), log: [...b.log, { at, by: who(x), action: `Hoàn tiền theo hồ sơ ${c.no}` }] } : b)),
    }));
    return { ok: true };
  },

  // ---------- Hoa hồng và tua ----------

  updateCommissionRate(group: string, patch: Partial<CommissionRate>): Result {
    if (cur().tier !== 'chief') return fail('Chỉ Kế toán trưởng sửa tỷ lệ');
    set(x => ({ ...x, commissionRates: x.commissionRates.map(r => (r.group === group ? { ...r, ...patch } : r)) }));
    return { ok: true };
  },

  /** Gán người: không đổi tiền nên làm được sau khi khóa sổ, có ghi nhật ký */
  setBillStaff(id: string, input: { sale: string; cskh: string; team: Team }): Result {
    if (cur().tier !== 'revenue') return fail('Kế toán nội bộ gán nhân sự');
    set(x => patchBill(x, id, b => ({ ...b, sale: input.sale.trim() || undefined, cskh: input.cskh.trim() || undefined, team: input.team, log: [...b.log, { at: Date.now(), by: who(x), action: 'Gán Sale, CSKH, ekip' }] })));
    return { ok: true };
  },

  setUsageTeam(id: string, team: Team, machine: string): Result {
    if (cur().tier !== 'revenue') return fail('Kế toán nội bộ gán nhân sự');
    if (!hasTeam(team)) return fail('Chọn Bác sĩ, KTV hoặc Điều dưỡng');
    set(x => ({ ...x, usageLog: x.usageLog.map(e => (e.id === id ? { ...e, team, machine: machine.trim() } : e)) }));
    return { ok: true };
  },

  /** Bổ sung chứng từ thanh toán cho hóa đơn mua vào */
  setInputPaid(id: string, payRef: string): Result {
    if (!payRef.trim()) return fail('Nhập mã chứng từ thanh toán');
    set(x => ({ ...x, inputInvoices: x.inputInvoices.map(i => (i.id === id ? { ...i, pay: 'bank' as const, payRef: payRef.trim().toUpperCase() } : i)) }));
    return { ok: true };
  },
};

/**
 * POS đổi (kể cả từ tab khác) thì Kế toán tự nhận — từ lúc Kế toán được dùng tới (cur()).
 * Lần đầu chưa có màn nào đăng ký nghe nên đồng bộ ngay trong lúc nạp không làm render lại giữa chừng.
 */
let syncing = false;
function startSync() {
  if (syncing) return;
  syncing = true;
  usePosStore.subscribe(() => { ktApi.syncPos(); ktApi.syncCards(); });
  useTreatmentStore.subscribe(() => { ktApi.syncCards(); });
  useExecLog.subscribe(() => { ktApi.syncExecLog(); });
  ktApi.syncPos();
  ktApi.syncCards();
  ktApi.syncExecLog();
}
